import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./authContext";
import ProtectedRoute from "./components/ProtectedRoute";

import AdminLayout from "layouts/admin/index";
import AuthLayout from "layouts/auth";
import MeetingLayout from "layouts/meeting/index";
import PhonePePayment from "views/payment/PhonePePayment";
import PaymentCallback from "views/payment/PaymentCallback";
import "assets/css/Plugins.css";
import { useState, useEffect, useCallback } from "react";
import { clarity } from 'clarity-js';
import PrivacyPolicy from './privacyPolicy.page';
import loadAnalytics from './load-analytics';
import { registerSw, requestNotificationPermission, subscribeForPush } from './registerSw';
import { savePushSubscription, type PushSubscriptionPayload } from './api/push.service';
import { storeIncomingCallData, generateRoomName, cleanPhoneNumber, getPhoneNumberId } from './utils/callUtils';
import { initAudioNotification, playCallNotification, stopCallNotification } from './utils/audioNotification';
import { logger } from './utils/logger';

const App = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleIncomingCall = useCallback((message: any) => {
    const callerMobile = message.caller_id || message.callerId || message.customerNumber;
    if (!callerMobile) {
      logger.error('No caller mobile number found in message:', message);
      return;
    }

    const phoneNumberId = getPhoneNumberId();
    const callerPhone = cleanPhoneNumber(callerMobile);
    
    // Generate and store room name
    let roomName: string | undefined;
    if (phoneNumberId && callerPhone) {
      try {
        roomName = generateRoomName(phoneNumberId, callerPhone);
      } catch (error) {
        logger.warn('Could not generate room name:', error);
      }
    }

    // Store call data - ensure status is set correctly
    const callStatus = message.call_status || message.callStatus || message.status || 'incoming';
    
    logger.log('Storing incoming call data:', {
      callerId: message.caller_id || message.callerId || callerMobile,
      callerPhone,
      roomName,
      callStatus,
      calleeId: message.callee_id,
    });
    
    storeIncomingCallData({
      callerId: message.caller_id || message.callerId || callerMobile,
      callerPhone,
      roomName,
      callStatus: callStatus,
      calleeId: message.callee_id,
    });

    // Only redirect if NOT already on whatsapp-send-message page
    const currentPath = window.location.pathname;
    const isOnWhatsAppPage = currentPath.includes('/whatsapp-send-message/');
    
    if (!isOnWhatsAppPage) {
      // User is not on the message page, redirect to call page
      const targetPath = `/events/whatsapp-send-message/${callerPhone}/whatsappcalls`;
      
      setTimeout(() => {
        try {
          navigate(targetPath);
        } catch (error) {
          logger.error('Navigation error, using window.location:', error);
          window.location.href = targetPath;
        }
      }, 100);
    }
    // If already on whatsapp-send-message page, just show header notification (no redirect)
  }, [navigate]);

  // Incoming calls via push only (WebSocket removed): SW postMessage triggers same UI
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data?.type !== 'incoming_call') return;
      const callerNumber = data.callerNumber || data.caller_number || '';
      if (!callerNumber) return;
      initAudioNotification();
      playCallNotification();
      const message = {
        caller_id: callerNumber,
        callerId: callerNumber,
        callee_id: data.phoneNumberId || data.phone_number_id,
        call_status: 'pending',
        callId: data.callId,
        callerName: data.callerName,
      };
      handleIncomingCall(message);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [handleIncomingCall]);

  // Expose stopCallNotification for components (Join call / Dismiss)
  useEffect(() => {
    (window as any).stopCallNotification = stopCallNotification;
    return () => {
      delete (window as any).stopCallNotification;
    };
  }, []);

  useEffect(() => {
    clarity.start({
      projectId: 'kgs19cm8ca',
      upload: 'https://m.clarity.ms/collect',
      track: true,
      content: true,
      cookies: ['user_email']
  })
  }, []);

  useEffect(() => {
    registerSw()
      .then((reg) => console.log("Service worker registered:", reg.scope))
      .catch((err) => console.warn("Service worker registration failed:", err));
  }, []);

  // Request notification permission and subscribe for push only after login.
  // Browser remembers permission and subscription; we save to backend when we have one.
  useEffect(() => {
    if (!isAuthenticated) return;
    const phoneNumberId = getPhoneNumberId();
    console.log("phoneNumberId:", phoneNumberId);
    (async () => {
      const permission = await requestNotificationPermission();
      console.log("Notification permission:", permission);
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (permission === "granted" && vapidPublicKey) {
        const subscription = await subscribeForPush(vapidPublicKey);
        if (subscription) {
          console.log("Push subscription:", subscription.toJSON());
          try {
            await savePushSubscription(
              subscription.toJSON() as PushSubscriptionPayload,
              getPhoneNumberId() || undefined
            );
          } catch (err) {
            console.warn("Failed to save push subscription to backend:", err);
          }
        }
      }
    })();
  }, [isAuthenticated]);
  
  // Create a new context
  const [themeApp, setThemeApp] = useState<any>({
    "--background-100": "#FFFFFF",
    "--background-900": "#070f2e",
    "--shadow-100": "rgba(112, 144, 176, 0.08)",
    "--color-50": "#E9E3FF",
    "--color-100": "#C0B8FE",
    "--color-200": "#A195FD",
    "--color-300": "#8171FC",
    "--color-400": "#7551FF",
    "--color-500": "#422AFB",
    "--color-600": "#3311DB",
    "--color-700": "#2111A5",
    "--color-800": "#190793",
    "--color-900": "#11047A",
  });
  const [mini, setMini] = useState(false);

  // When the theme state changes, this effect will update the CSS variables in the document's root element
  useEffect(() => {
    let color;
    for (color in themeApp) {
      document.documentElement.style.setProperty(color, themeApp[color]);
    }
    //eslint-disable-next-line
  }, [themeApp]);

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/:link1/:link2" element={<MeetingLayout />} />
        <Route path="/pdf" element={<MeetingLayout />} />
        <Route path="PrivacyPolicy/*" element={<PrivacyPolicy />} />
        <Route path="/payment" element={<PhonePePayment />} />
        <Route path="/payment.html" element={<PhonePePayment />} />
        <Route path="/payment-callback" element={<PaymentCallback />} />
        <Route path="/payment-callback.html" element={<PaymentCallback />} />
        
        {/* Auth routes - only accessible when NOT authenticated */}
        <Route path="signin/*" element={
          <ProtectedRoute requireAuth={false}>
            <AuthLayout />
          </ProtectedRoute>
        } />
        
        {/* Protected admin routes - only accessible when authenticated */}
        <Route path="linkedin/*" element={
          <ProtectedRoute requireAuth={true}>
            <AdminLayout
              setMini={setMini}
              mini={mini}
              theme={themeApp}
              setTheme={setThemeApp}
            />
          </ProtectedRoute>
        } />

        <Route path="events/*" element={
          <ProtectedRoute requireAuth={true}>
            <AdminLayout
              setMini={setMini}
              mini={mini}
              theme={themeApp}
              setTheme={setThemeApp}
            />
          </ProtectedRoute>
        } />
        
        {/* Redirect old paths to new /events structure */}
        <Route path="/whatsapp-phone-numbers-list" element={<Navigate to="/events/conversations" replace />} />
        <Route path="/conversations" element={<Navigate to="/events/conversations" replace />} />
        <Route path="/finance" element={<Navigate to="/events/finance" replace />} />
        <Route path="/finance/payments" element={<Navigate to="/events/finance/payments" replace />} />
        <Route path="/finance/invoices" element={<Navigate to="/events/finance/invoices" replace />} />
        <Route path="/finance/reports" element={<Navigate to="/events/finance/reports" replace />} />
        <Route path="/finance/taxes" element={<Navigate to="/events/finance/taxes" replace />} />
        <Route path="/ecommerce" element={<Navigate to="/events/ecommerce" replace />} />
        <Route path="/ecommerce/products" element={<Navigate to="/events/ecommerce/products" replace />} />
        <Route path="/ecommerce/orders" element={<Navigate to="/events/ecommerce/orders" replace />} />
        <Route path="/ecommerce/customers" element={<Navigate to="/events/ecommerce/customers" replace />} />
        <Route path="/ecommerce/analytics" element={<Navigate to="/events/ecommerce/analytics" replace />} />
        <Route path="/membership" element={<Navigate to="/events/membership" replace />} />
        <Route path="/membership/plans" element={<Navigate to="/events/membership/plans" replace />} />
        <Route path="/membership/subscriptions" element={<Navigate to="/events/membership/subscriptions" replace />} />
        <Route path="/membership/members" element={<Navigate to="/events/membership/members" replace />} />
        <Route path="/membership/benefits" element={<Navigate to="/events/membership/benefits" replace />} />
        <Route path="/whatsapp-marketing-list" element={<Navigate to="/events/whatsapp-marketing-list" replace />} />
        <Route path="/whatsapp-list-template" element={<Navigate to="/events/whatsapp-list-template" replace />} />
        <Route path="/user-settings" element={<Navigate to="/events/user-settings" replace />} />
        <Route path="/business-personality-settings" element={<Navigate to="/events/business-personality-settings" replace />} />
        <Route path="/whatsapp-phone-groups-manager" element={<Navigate to="/events/whatsapp-phone-groups-manager" replace />} />
        <Route path="/whatsapp-flow-builder" element={<Navigate to="/events/whatsapp-flow-builder" replace />} />
        <Route path="/automated-flow-builder" element={<Navigate to="/events/automated-flow-builder" replace />} />
        <Route path="/automated-flows-list" element={<Navigate to="/events/automated-flows-list" replace />} />
        <Route path="/file-upload" element={<Navigate to="/events/file-upload" replace />} />
        <Route path="/phone-settings" element={<Navigate to="/events/phone-settings" replace />} />
        
        {/* Default redirect based on auth status */}
        <Route path="/" element={
          isAuthenticated ? 
            <Navigate to="/events/" replace /> : 
            <Navigate to="/signin" replace />
        } />
    </Routes>
    </>
  );
};

loadAnalytics();
export default App;
